import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import axios from "axios";
import { useToast } from "@/utils";
import { newAuthRepository } from "@/repositories/auth";
import { client } from "@/api/client";
import { useForm } from "react-hook-form";
import { FormSubmitButton } from "@/components/base/ui/FormSubmitButton";
import { emailValidate } from "@/validator/email";
import Link from "next/link";
import { Head } from "@/components/base/Head";
import { SectionTitle } from "@/components/base/ui/SectionTitle";
import { Breadcrumb } from "@/components/base/Breadcrumb";
import { fetchCurrentUser } from "@/requests/currentUser";
import { useSetRecoilState } from "recoil";
import { currentUserState } from "@/states/currentUser";
import { useCurrentUser } from "@/hoc/useCurrentUser";

const SignInPage = () => {
  const META_TITLE = "会員ログイン";
  const breadcrumbs = [
    {
      title: META_TITLE,
    },
  ];
  const router = useRouter();
  const repository = newAuthRepository(client);
  const setCurrentUser = useSetRecoilState(currentUserState);
  const { isAuthChecking, currentUser } = useCurrentUser();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const onFinish = async (data: any) => {
    setLoading(true);
    try {
      const response = await repository.signIn(data);
      const authHeaders = {
        "access-token": response["access-token"],
        client: response["client"],
        expiry: response["expiry"],
        uid: response["uid"],
      };
      await axios.post("/api/signin", {
        authHeaders: JSON.stringify(authHeaders),
      });
      toast.success("ログインに成功しました。");
      const currentUser = await fetchCurrentUser();
      setCurrentUser(currentUser);
      await router.replace(
        router.query.redirect_to ? (router.query.redirect_to as string) : "/app"
      );
    } catch (e) {
      if (e === 401) {
        toast.error("メールアドレスもしくはパスワードが間違っております。");
      } else {
        toast.error(
          "エラーが発生しました。お手数ですがしばらく時間を開けて再度お試しください。"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthChecking) return;
    if (!currentUser) return;
  }, [isAuthChecking, currentUser]);

  useEffect(() => {
    if (router.query.account_confirmation_success === "true") {
      router.push("/registrations");
    }
  }, [router]);

  return (
    <>
      <Head title={META_TITLE} canonicalPath={"/signin"} />
      <main className={"md:bg-gray-100 pb-20"}>
        <div className={"mb-10 max-w-[1440px] m-auto px-4 py-2"}>
          <Breadcrumb breadcrumbs={breadcrumbs} />
        </div>
        <div className={"max-w-[960px] bg-white m-auto p-5 md:p-10 rounded"}>
          <div className={"mb-8"}>
            <SectionTitle title={"会員ログイン"} isBorder={false} />
          </div>
          <form
            onSubmit={handleSubmit((data) => onFinish(data))}
            className={""}
          >
            <div className={"mb-10"}>
              <div className={"mb-5"}>
                <label className={"input-label"}>メールアドレス</label>
                <input
                  type="email"
                  className={"input-text"}
                  {...register("email", {
                    required: true,
                    validate: async (value) => await emailValidate(value),
                  })}
                />
                {errors.email && (
                  <span className={"block text-red-500 mt-2 text-sm"}>
                    {"メールアドレスを入力してください。"}
                  </span>
                )}
              </div>
              <div>
                <label className={"input-label"}>パスワード</label>
                <input
                  type="password"
                  className={"input-text"}
                  {...register("password", { required: true })}
                />
                {errors.password && (
                  <span className={"block text-red-500 mt-2 text-sm"}>
                    {"パスワードを入力してください。"}
                  </span>
                )}
              </div>
            </div>
            <FormSubmitButton loading={loading}>ログイン</FormSubmitButton>
          </form>
          <div className={"mt-8 text-sm underline"}>
            <Link href="/password/new">パスワードをお忘れですか</Link>
          </div>
          <div className={"mt-4 text-sm underline"}>
            <Link href="/registrations/new">新規会社登録の方はこちら</Link>
          </div>
        </div>
      </main>
    </>
  );
};

export async function getStaticProps() {
  return {
    props: {},
  };
}
export default SignInPage;
